import { lazy, Suspense } from 'react';
import PrimaryButton from '@/components/ui/primary-button';
import { useDisclosure } from '@/components/ui/lib/use-disclosure';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/utils/cn';
import { DICTIONARY } from '@/config/dictionary';
import { isMockMode } from '@/config/env';
import { APP_PATHS } from '@/config/routes/app-paths';
import { ensureValidPath } from '@/utils/validate-path';
import type { SignInButtonProps } from '@/features/auth/types/sign-in-button';

// Dev-only UI. The lazy() call must stay inside the isMockMode branch: a top-level
// lazy(() => import(...)) is emitted as its own chunk even when nothing can reach it,
// so a mocks-off build would still publish the picker and its inlined mock-db seed.
// Folding the expression to null lets Rollup drop the dynamic import entirely.
const MockUserPicker = isMockMode
  ? lazy(() => import('@/features/auth/components/mock-user-picker'))
  : null;

const SignInButton = ({ className, children }: SignInButtonProps) => {
  const navigate = useNavigate();
  const { open: pickerOpen, onOpen: openPicker, onClose: closePicker } = useDisclosure();

  const handleClick = () => {
    if (isMockMode) {
      openPicker();
      return;
    }

    void navigate({ to: ensureValidPath(APP_PATHS.home), search: { tab: 'signin' } }).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  return (
    <>
      <PrimaryButton fontWeight="semibold" onClick={handleClick} className={cn(className)}>
        {children ?? DICTIONARY.auth.signIn.idle}
      </PrimaryButton>
      {MockUserPicker && (
        <Suspense>
          <MockUserPicker
            open={pickerOpen}
            onClose={() => {
              closePicker();
            }}
          />
        </Suspense>
      )}
    </>
  );
};

export default SignInButton;
